import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { acceptStaffInvitation } from "../../../../lib/api/manager.api";

type AcceptState = "loading" | "success" | "error";

export default function AcceptStaffInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasRequested = useRef(false);

  const [state, setState] = useState<AcceptState>("loading");
  const [message, setMessage] = useState("Accepting invitation...");

  const token = searchParams.get("token");

  useEffect(() => {
    if (hasRequested.current) return;

    if (!token) {
      setState("error");
      setMessage("Missing invite token.");
      return;
    }

    hasRequested.current = true;

    const run = async () => {
      try {
        const data = await acceptStaffInvitation(token);
        setState("success");
        setMessage(data?.message || "Invitation accepted. Redirecting to profile...");
        setTimeout(() => navigate("/profile"), 1200);
      } catch (error: any) {
        setState("error");
        setMessage(error?.message || "Failed to accept invitation.");
      }
    };

    run();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-[#E8E4DC] rounded-2xl p-6 shadow-sm">
        <div className="flex items-start gap-3">
          {state === "loading" && (
            <Loader2 className="w-5 h-5 text-[#C17B3F] mt-0.5 animate-spin" />
          )}
          {state === "success" && (
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
          )}
          {state === "error" && (
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          )}
          <div>
            <h1 className="text-lg font-semibold text-[#1C1917]">
              Staff Invitation
            </h1>
            <p className="text-sm text-[#6B6460] mt-1">{message}</p>
          </div>
        </div>

        {state === "error" && (
          <button
            onClick={() => navigate("/profile")}
            className="mt-5 w-full rounded-xl bg-[#FFD166] text-[#111827] px-4 py-2.5 text-sm font-semibold hover:bg-[#E1AD01] transition-colors"
          >
            Go to Profile
          </button>
        )}
      </div>
    </div>
  );
}
